"use client";

import { SlidersHorizontal } from "lucide-react";
import { useRouter } from "next/navigation";
import { type FormEventHandler, type ReactNode, useCallback } from "react";

import { Button } from "@/components/ui/button";
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetFooter,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@/components/ui/sheet";

import FilterIcon from "@/components/icons/FilterIcon";
import { useTempQueryParamsMut } from "@/lib/hooks";
import { isSafePublicCategoryListingPath } from "@/lib/public-category-routes";
import { toSafeQueryString } from "@/lib/utils";
import { FilterSheetContextProvider } from "@/components/sheets/FilterSheet/context";
import {
	listingFiltersGlassPillClassName,
	listingToolbarIconCircleClassName,
	listingToolbarLucideIconClassName,
} from "@/components/sheets/FilterSheet/filter-sheet-glass";
import { cn } from "@/lib/utils";

export type Props = {
	children: ReactNode;
	removeParamsWhen: Record<string, string>;
	/** Lucide sliders icon for public listing toolbars; legacy SVG elsewhere (e.g. admin). */
	glassToolbar?: boolean;
};

export default function FilterSheet({
	removeParamsWhen,
	children,
	glassToolbar = false,
}: Props) {
	const router = useRouter();
	const { pathname, mutSearchParams, setSearchParam } = useTempQueryParamsMut(removeParamsWhen);

	const onFormSubmit: FormEventHandler<HTMLFormElement> = useCallback(
		(e) => {
			e.preventDefault();

			const temp = new URLSearchParams(mutSearchParams);
			const data = new FormData(e.currentTarget).entries();

			let filterNavigate: string | null = null;
			let limitFromForm: string | undefined;
			for (const [key, value] of data) {
				const s = value.toString();
				if (key === "filterNavigate") {
					filterNavigate = s || null;
					continue;
				}
				if (key === "limit") {
					limitFromForm = s;
					continue;
				}
				if (!s) {
					if (key === "categories" || key === "subcategoryIds") {
						temp.delete(key);
					}
					continue;
				}
				temp.set(key, s);
			}

			if (limitFromForm !== undefined && limitFromForm !== "") {
				const normalized = limitFromForm === "50" ? "50" : "12";
				const prev = mutSearchParams.get("limit") || "12";
				if (prev !== normalized) {
					temp.delete("page");
				}
				if (normalized === "12") {
					temp.delete("limit");
				} else {
					temp.set("limit", "50");
				}
			}

			if (filterNavigate && isSafePublicCategoryListingPath(filterNavigate)) {
				temp.delete("page");
				const q = toSafeQueryString(temp);
				router.push(q ? `${filterNavigate}?${q}` : filterNavigate);
				return;
			}

			const query = toSafeQueryString(temp);
			router.push(query ? `${pathname}?${query}` : pathname);
		},
		[mutSearchParams, pathname, router],
	);

	return (
		<FilterSheetContextProvider value={{ pathname, mutSearchParams, setSearchParam }}>
			<Sheet>
				<SheetTrigger asChild>
					<Button
						aria-label="Open filters"
						variant="ghost"
						className={cn(
							glassToolbar
								? cn(
										listingToolbarIconCircleClassName,
										"hover:bg-white/50 dark:hover:bg-[rgba(11,21,27,0.78)]",
									)
								: cn(
										listingFiltersGlassPillClassName,
										"overflow-hidden px-4 sm:px-5",
									),
						)}
					>
						{glassToolbar ? (
							<SlidersHorizontal
								className={listingToolbarLucideIconClassName}
								strokeWidth={2.25}
								aria-hidden
							/>
						) : (
							<>
								<FilterIcon className="mr-2 shrink-0" aria-hidden />
								<span className="truncate">Filters</span>
							</>
						)}
					</Button>
				</SheetTrigger>
				<SheetContent className="flex flex-col overflow-hidden">
					<form
						className="flex min-h-0 min-w-72 flex-1 flex-col px-2 sm:px-10"
						onSubmit={onFormSubmit}
					>
						<SheetHeader className="mb-4 flex-shrink-0 pt-10 text-left">
							<SheetTitle className="font-medium text-white">All Filters</SheetTitle>
							<hr className="mb-4 border-0 border-t-[0.5px] border-solid border-t-[color:var(--color-brand-primary)]" />
						</SheetHeader>

						<div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch] pr-1">
							{children}
						</div>

						<SheetFooter className="mt-4 flex flex-shrink-0 !flex-row !justify-center border-0 border-t-[0.5px] border-solid border-t-[color:var(--color-brand-primary)] pt-4">
							<SheetClose asChild>
								<Button
									type="submit"
									className="custom-box-shadow w-full py-6 font-semibold"
								>
									Apply
								</Button>
							</SheetClose>
						</SheetFooter>
					</form>
				</SheetContent>
			</Sheet>
		</FilterSheetContextProvider>
	);
}
